import { createContext, useContext, useEffect, useRef } from "react";
import { FacebookPixel } from "react-use-facebook-pixel";
import { MemberLayoutInfo_collections } from "../../../types/__generated__/MemberLayoutInfo";

/* Stable per-mount id. React 18's useId would do this in one line, but
 * the repo pins @types/react@17, which doesn't expose the type — using
 * the runtime export trips tsc. A useRef-backed counter is enough for
 * what these hooks need (a key to push/pop from a stack, never read by
 * users). Bumped at first read and cached on the ref. */
let topInsetIdCounter = 0;
const useStableId = () => {
  const ref = useRef<string | null>(null);
  if (ref.current === null) {
    topInsetIdCounter += 1;
    ref.current = `top-inset-${topInsetIdCounter}`;
  }
  return ref.current;
};

// ===========
// App Context
// ===========
interface AppContextProps {
  memberId: number | undefined | null;
  memberTotalPoints: number | undefined | null;
  memberHasTakenTheQuiz: boolean | undefined | null;
  memberFirstName: string | undefined | null;
  memberCurrentLevel: number | undefined | null;
  memberCurrentLevelName: string | undefined | null;
  memberHasDownloadedMobileApp: boolean | undefined | null;
  memberTotalChargedPersonalOrders: number | undefined | null;
  minBottlesForFreeShipping: number | undefined | null;
  minBottlesInOrder: number | undefined | null;
  standardShippingFee?: number | undefined | null;
  shouldWinesBeAddedToSubscription: boolean | undefined | null;
  collections: (MemberLayoutInfo_collections | null)[] | undefined | null;
  isIterableReady: boolean;
  facebookPixel: FacebookPixel | null;
}

const appInitialState: AppContextProps = {
  memberId: null,
  memberTotalPoints: null,
  memberHasTakenTheQuiz: null,
  memberFirstName: "",
  memberCurrentLevel: null,
  memberCurrentLevelName: null,
  memberHasDownloadedMobileApp: null,
  memberTotalChargedPersonalOrders: null,
  minBottlesForFreeShipping: null,
  minBottlesInOrder: null,
  standardShippingFee: null,
  shouldWinesBeAddedToSubscription: null,
  collections: null,
  isIterableReady: false,
  facebookPixel: null,
};

export const AppContext = createContext<AppContextProps>(appInitialState);
export const useAppContext = () => useContext(AppContext);

// ===========
// Layout Context
// ===========

/* Top-inset stack: every page-leading element that wants the safe-area
 * notch zone painted with a particular bg colour pushes onto a stack.
 * What the TopInsetSpacer paints is picked in priority order:
 *
 *   1. The most-recent EXPLICIT push (useTopInsetBg) — used by inner
 *      heroes / panels that visually own the top edge of the screen
 *      (e.g. MainBottleInfo's wine-class colour, LogInPanel's blue).
 *      These describe a specific visual element on the page, and
 *      should always beat structural defaults.
 *
 *   2. The most-recent DEFAULT push (useTopInsetBgDefault) — used by
 *      structural wrappers that just want the inset to match their
 *      bg as a fallback (TrayContainer's white, the cart strip's
 *      coral). React effects fire leaf-first, which means an outer
 *      TrayContainer's effect runs AFTER an inner hero's — without
 *      this priority split a "default" push from the wrapper would
 *      always bury the inner hero's explicit colour.
 *
 *   3. colors.white — fallback when both stacks are empty.
 *
 * This replaces the previous distributed pattern where every hero /
 * tray header / sheet head individually folded env(safe-area-inset-
 * top) into its padding. Layout owns it now; consumers just declare
 * their colour via useTopInsetBg(color) (or useTopInsetBgDefault for
 * the fallback variant). */
interface LayoutContextProps {
  setShouldOpenBoxAndCartTray: (open: boolean) => void;
  setShowHeaderBarFromChild: (show: boolean) => void;
  showHeaderBarFromChild: boolean;
  pushTopInsetBg: (id: string, color: string) => void;
  popTopInsetBg: (id: string) => void;
  pushTopInsetBgDefault: (id: string, color: string) => void;
  popTopInsetBgDefault: (id: string) => void;
}

const layoutInitialState: LayoutContextProps = {
  setShouldOpenBoxAndCartTray: () => {},
  setShowHeaderBarFromChild: () => {},
  showHeaderBarFromChild: true,
  pushTopInsetBg: () => {},
  popTopInsetBg: () => {},
  pushTopInsetBgDefault: () => {},
  popTopInsetBgDefault: () => {},
};

export const LayoutContext = createContext<LayoutContextProps>(layoutInitialState);
export const useLayoutContext = () => useContext(LayoutContext);

/* Each page mounts its own Layout, so a toast that outlives a navigation is
 * left holding the previous page's setShouldOpenBoxAndCartTray, which opens
 * nothing. Asking by event reaches whichever Layout is on screen now. */
const OPEN_BOX_AND_CART_TRAY_EVENT = "openBoxAndCartTray";

export const requestOpenBoxAndCartTray = () => window.dispatchEvent(new Event(OPEN_BOX_AND_CART_TRAY_EVENT));

/** Returns an unsubscribe function. */
export const onOpenBoxAndCartTrayRequest = (listener: () => void) => {
  window.addEventListener(OPEN_BOX_AND_CART_TRAY_EVENT, listener);
  return () => window.removeEventListener(OPEN_BOX_AND_CART_TRAY_EVENT, listener);
};

/* Declare the colour the safe-area notch zone should paint while the
 * calling component is mounted. Pass null / undefined to opt out.
 * Beats anything pushed via useTopInsetBgDefault.
 *
 * Use this for the visual element that "owns" the top of the screen —
 * a coloured hero, a login panel's blue strip, the wine-class tinted
 * ProductHero. The hook re-registers when the colour changes. */
export const useTopInsetBg = (color: string | null | undefined) => {
  const id = useStableId();
  const { pushTopInsetBg, popTopInsetBg } = useContext(LayoutContext);
  useEffect(() => {
    if (!color) return undefined;
    pushTopInsetBg(id, color);
    return () => popTopInsetBg(id);
  }, [id, color, pushTopInsetBg, popTopInsetBg]);
};

/* Lower-priority variant. Fallback colour for structural wrappers
 * (TrayContainer's white, Layout's cart-strip coral) that should
 * paint the inset only when no inner hero has called useTopInsetBg.
 *
 * Why a separate hook: React effects fire leaf-first. Without a
 * priority split, the outer wrapper's effect runs AFTER the inner
 * hero's, and a single most-recent-wins stack would let the wrapper's
 * white bury the hero's actual colour. */
export const useTopInsetBgDefault = (color: string | null | undefined) => {
  const id = useStableId();
  const { pushTopInsetBgDefault, popTopInsetBgDefault } = useContext(LayoutContext);
  useEffect(() => {
    if (!color) return undefined;
    pushTopInsetBgDefault(id, color);
    return () => popTopInsetBgDefault(id);
  }, [id, color, pushTopInsetBgDefault, popTopInsetBgDefault]);
};
